import React, { useState, useRef, useEffect, forwardRef } from "react";
import HTMLFlipBook from "react-pageflip";
import { pdfjs, Document, Page as ReactPdfPage } from "react-pdf";
import {
    Box,
    IconButton,
    Typography,
    CircularProgress,
    Stack,
    Backdrop,
} from "@mui/material";
import { NavigateBefore, NavigateNext } from "@mui/icons-material";
import { Icon } from "@iconify/react/dist/iconify.js";
import CustomDivider from "../../utils/CustomDivider";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const Page = forwardRef((props, ref) => (
    <div
        ref={ref}
        style={{
            backgroundColor: "#fff",
            boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
            borderRadius: 8,
            overflow: "hidden",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 10,
        }}
    >
        {props.children}
    </div>
));

const NavigationControls = ({
    numPages,
    currentPage,
    goToPage,
    setGoToPage,
    onPrev,
    onNext,
    onGo,
}) => (
    <>
        <Stack
            direction="row"
            spacing={4}
            alignItems="center"
            justifyContent="center"

        >
            <IconButton
                onClick={onPrev}
                sx={{
                    color: "white",
                    border: "1px solid #444",
                    bgcolor: "#333",
                    "&:hover": { bgcolor: "#555" },
                }}
            >
                <NavigateBefore fontSize="small" />
            </IconButton>

            <Typography variant="body1" color="#fff">
                Page {currentPage + 1} of {numPages}
            </Typography>

            <IconButton
                onClick={onNext}
                sx={{
                    color: "white",
                    border: "1px solid #444",
                    bgcolor: "#333",
                    "&:hover": { bgcolor: "#555" },
                }}
            >
                <NavigateNext fontSize="small" />
            </IconButton>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center" mt={2}>
            <input
                type="number"
                min={1}
                max={numPages}
                value={goToPage}
                onChange={(e) => setGoToPage(e.target.value)}
                placeholder="Page #"
                style={{
                    width: 80,
                    padding: 6,
                    borderRadius: 4,
                    border: "1px solid #555",
                    background: "#222",
                    color: "#fff",
                }}
            />

            <IconButton
                onClick={onGo}
                sx={{
                    color: "white",
                    bgcolor: "#333",
                    border: "1px solid #444",
                }}
            >
                Go
            </IconButton>
        </Stack>
    </>
);

const FlipBookView = ({
    pdfUrl,
    bookRef,
    numPages,
    onDocumentLoadSuccess,
    onPage,
    width,
    height,
    pageWidth,
    htmlFlipBookWidth,
   isMaximize=false
}) => (
    <Document
        file={pdfUrl}
        onLoadSuccess={onDocumentLoadSuccess}
        loading={<CircularProgress color="inherit" />}
    >
        {numPages && (
            <Box sx={{ position: "relative", width, marginBottom: 10 }}>
                <HTMLFlipBook
                    width={isMaximize?pageWidth-100:htmlFlipBookWidth}
                    height={height}
                    size="stretch"
                    onFlip={onPage}
                    ref={bookRef}
                    showCover={true}
                    className="flip-book"
                    style={{ margin: "2rem auto" }}
                >
                    {[...Array(numPages).keys()].map((pIndex) => (
                        <Page key={pIndex}>
                            <ReactPdfPage
                                pageNumber={pIndex + 1}
                                width={pageWidth}
                                renderAnnotationLayer={false}
                                renderTextLayer={false}
                            />
                        </Page>
                    ))}
                </HTMLFlipBook>
            </Box>
        )}
    </Document>
);

const PdfFlipbook = ({ uri }) => {
    const normalBookRef = useRef();
    const fullscreenBookRef = useRef();
    const fullscreenRef = useRef(null);

    const [numPages, setNumPages] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [goToPage, setGoToPage] = useState("");
    const [isMaximize, setIsMaximize] = useState(false);

    const pdfUrl = uri;

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
    };

    const onPage = (e) => {
        setCurrentPage(e.data);
    };

    const getActiveBook = () => {
        return isMaximize
            ? fullscreenBookRef.current
            : normalBookRef.current;
    };

    useEffect(() => {
        if (isMaximize && fullscreenRef.current) {
            fullscreenRef.current.requestFullscreen();
        } else if (!isMaximize && document.fullscreenElement) {
            document.exitFullscreen();
        }
    }, [isMaximize]);

    useEffect(() => {
        const onFsChange = () => {
            if (!document.fullscreenElement) {
                setIsMaximize(false);
            }
        };
        document.addEventListener("fullscreenchange", onFsChange);
        return () =>
            document.removeEventListener("fullscreenchange", onFsChange);
    }, []);

    const handlePrev = () =>
        getActiveBook()?.pageFlip()?.flipPrev();

    const handleNext = () =>
        getActiveBook()?.pageFlip()?.flipNext();

    const handleGo = () => {
        const page = Number(goToPage);
        if (!page || page < 1 || page > numPages) return;
        getActiveBook()?.pageFlip()?.flip(page - 1);
    };

    return (
        <>
            {/* NORMAL VIEW */}
            <Box
                sx={{
                    bgcolor: "#1a1a1a",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "start",
                    position: "relative",
                    height: 650
                }}
            >
                <FlipBookView
                    pdfUrl={pdfUrl}
                    bookRef={normalBookRef}
                    numPages={numPages}
                    onDocumentLoadSuccess={onDocumentLoadSuccess}
                    onPage={onPage}
                    width={600}
                    height={215}
                    pageWidth={300}
                    htmlFlipBookWidth={150}
                />
                {/* <Box sx={{ height: 150, width: "20%" }}>

                </Box> */}

                {/* <CustomDivider /> */}
                <NavigationControls
                    numPages={numPages}
                    currentPage={currentPage}
                    goToPage={goToPage}
                    setGoToPage={setGoToPage}
                    onPrev={handlePrev}
                    onNext={handleNext}
                    onGo={handleGo}
                />

                <IconButton
                    onClick={() => setIsMaximize(true)}
                    sx={{
                        color: "white",
                        bgcolor: "#333",
                        border: "1px solid #444",
                        position: "absolute",
                        bottom: 5,
                        right: 5,
                    }}
                >
                    <Icon
                        icon="dinkie-icons:arrows-maximize"
                        width="15"
                        height="15"
                    />
                </IconButton>
            </Box>

            {/* FULLSCREEN VIEW */}
            <Backdrop
                ref={fullscreenRef}
                open={isMaximize}
                sx={{
                    backgroundColor: "rgba(0, 0, 0, 0.9)",
                    color: "#fff",
                    zIndex: (theme) => theme.zIndex.modal + 1,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 4,
                        position: "relative",
                    }}
                >
                    <FlipBookView
                        pdfUrl={pdfUrl}
                        bookRef={fullscreenBookRef}
                        numPages={numPages}
                        onDocumentLoadSuccess={onDocumentLoadSuccess}
                        onPage={onPage}
                        width={900}
                        height={470}
                        pageWidth={470}
                        htmlFlipBookWidth={235}
                        isMaximize={true}
                    />
                    <Box sx={{ height: 25, width: "20%" }}>

                    </Box>

                    <NavigationControls
                        numPages={numPages}
                        currentPage={currentPage}
                        goToPage={goToPage}
                        setGoToPage={setGoToPage}
                        onPrev={handlePrev}
                        onNext={handleNext}
                        onGo={handleGo}
                    />

                    <IconButton
                        onClick={() => setIsMaximize(false)}
                        sx={{
                            color: "white",
                            bgcolor: "#333",
                            border: "1px solid #444",
                            position: "absolute",
                            bottom: 5,
                            right: 5,
                        }}
                    >
                        <Icon
                            icon="dinkie-icons:arrows-maximize"
                            width="15"
                            height="15"
                        />
                    </IconButton>
                </Box>
            </Backdrop>
        </>
    );
};

export default PdfFlipbook;
